import { FadeUp, Eyebrow } from "./motion";
import { Zap, Droplets, Shield, Feather, Cpu, Activity } from "lucide-react";

const MECH_IMG =
  "https://images.unsplash.com/photo-1711199694531-e982a79ea381?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwbWVjaGFuaWNhbCUyMG1hY2hpbmVyeXxlbnwwfHx8fDE3ODQ1NDIxMTF8MA&ixlib=rb-4.1.0&q=85";

const Cell = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`relative border border-[#27272a] bg-[#121212] p-8 group hover:border-[#3f3f46] transition-colors duration-300 ${className}`}
  >
    {children}
  </div>
);

const SpecItem = ({ icon: Icon, title, desc }) => (
  <div>
    <Icon className="w-6 h-6 text-[#ff4500] mb-4" strokeWidth={1.5} />
    <h3 className="font-display text-xl text-[#f5f5f5] mb-2">{title}</h3>
    <p className="text-sm leading-relaxed text-[#a1a1aa]">{desc}</p>
  </div>
);

const Features = () => {
  return (
    <section id="technology" data-testid="technology-section" className="py-24 lg:py-36">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <FadeUp>
          <Eyebrow num="01">The Technology</Eyebrow>
        </FadeUp>
        <FadeUp delay={0.05}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#f5f5f5] mt-6 max-w-3xl leading-[1.02]">
            Soft where it touches. <br />
            <span className="text-[#71717a]">Hard where it counts.</span>
          </h2>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mt-16">
          {/* Big spotlight product photo */}
          <FadeUp className="md:col-span-7" delay={0.05}>
            <Cell testid="tech-cell-photo" className="p-0 overflow-hidden h-full min-h-[360px]">
              <div className="relative h-full">
                <img
                  src={MECH_IMG}
                  alt="Spring and hydraulic actuator mechanism"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 min-h-[360px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 p-8">
                  <span className="font-mono-accent text-[11px] tracking-widest uppercase text-[#ff4500]">Core Mechanism</span>
                  <h3 className="font-display text-2xl lg:text-3xl text-white mt-2 max-w-sm">
                    Hybrid spring-hydraulic actuation
                  </h3>
                </div>
              </div>
            </Cell>
          </FadeUp>

          {/* Two stacked specs */}
          <div className="md:col-span-5 grid grid-cols-1 gap-4">
            <FadeUp delay={0.1}>
              <Cell testid="tech-cell-springs">
                <SpecItem
                  icon={Zap}
                  title="Tuned Spring System"
                  desc="Precision-calibrated springs store energy as you bend and release it as you lift — returning force exactly when your body needs it."
                />
              </Cell>
            </FadeUp>
            <FadeUp delay={0.15}>
              <Cell testid="tech-cell-hydraulics">
                <SpecItem
                  icon={Droplets}
                  title="Damped Hydraulics"
                  desc="Micro-hydraulic dampers smooth every motion, absorbing shock and controlling descent to protect the lower back and knees."
                />
              </Cell>
            </FadeUp>
          </div>

          {/* Bottom row: 4 feature cells */}
          {[
            { icon: Feather, title: "Soft Shell", desc: "Breathable textile harness that moves with you — no rigid cage, all-day comfort." },
            { icon: Shield, title: "Injury Guard", desc: "Reduces peak spinal compression during repetitive lifting cycles." },
            { icon: Cpu, title: "Passive Core", desc: "Zero electronics, zero charging. Mechanical assist that never runs out." },
            { icon: Activity, title: "Natural Range", desc: "Full freedom of movement — walk, twist, climb and crouch unrestricted." },
          ].map((s, i) => (
            <FadeUp key={i} className="md:col-span-3" delay={0.1 + i * 0.05}>
              <Cell testid={`tech-cell-feature-${i}`} className="h-full">
                <SpecItem {...s} />
              </Cell>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
